import { stringify } from "querystring";
import { ChipGroup } from "@newsela/angelou";
import { ReactNode } from "react";

import { useRouter } from "next/router";
import { AIChip } from "@/components/AIComponents/AIChip";
import AiLunaTagFullIcon from "@/icons/AILunaTagFull/AILunaTagFullIcon";
import AIBetaTag from "@/components/AIComponents/AIBetaTag/AIBetaTag";
import AIButton from "@/components/AIComponents/AiButton";
import AIToggle from "@/components/AIComponents/AIToggle";
import AILoadingSpinner from "@/components/AIComponents/AILoadingSpinner";
import LunaPeekingSideIcon from "@/icons/LunaPeekingSideIcon/LunaPeekingSideIcon";
import { useFilterControls } from "@/search/Langston/useFilterControls";
import LunaSearchingIcon from "@/icons/LunaSearchingIcon/LunaSearchingIcon";
import LunaConfusedIcon from "@/icons/LunaConfusedIcon/LunaConfusedIcon";
import LunaThinkingIcon from "@/icons/LunaThinkingIcon/LunaThinkingIcon";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import VisibilityPermissions from "../VisibilityPermissions/VisibilityPermissions";
import { LUNA_FILTER_PANEL_TEST_IDS } from "./LunaFilterPanel.constants";
import { LunaFilterPanelProps } from "./LunaFilterPanel.types";
import {
  panelRootCss,
  panelCardCss,
  badgesRowCss,
  sectionTitleCss,
  sectionDescriptionCss,
  subsectionTitleCss,
  chipsGroupCss,
  chipItemWrapperCss,
  primaryActionButtonCss,
  toggleWrapperCss,
  promptSectionCss,
  illustrationAffixTopRightCss,
  illustrationAffixBottomRightCss,
  textAreaOverridesCss,
  emptyStateContainerCss,
  loadingBoxCss,
  refinementAreaRootCss,
  refinementLoadingBoxCss,
  refinementLoadingHiddenCss,
  refinementLoadingVisibleCss,
  visibilityPermissionCss,
  lunaFullTagCss,
  labelPromptCss,
  textareaContainerCss,
} from "./LunaFilterPanel.styles";
import { formatSelectedFilters, LunaChip } from "./LunaFilterPanel.utils";

export const LunaFilterPanel = ({
  className,
  testId,
  isAISearchEnabled,
  onToggle,
}: LunaFilterPanelProps) => {
  const { push, query, pathname } = useRouter();
  const {
    getters: { selectedLunaChips },
    handlers: { setSelectedLunaChips },
  } = useSearchPageContext();

  const { handleRemoveFilter } = useFilterControls();

  const filteredValues = formatSelectedFilters(selectedLunaChips || []);

  const handleChipEvent = (_: unknown, value: string) => {
    handleRemoveFilter("", value);
    setSelectedLunaChips(
      selectedLunaChips.filter((chip: LunaChip) => {
        const chipValueFormatted = `${chip.type},${chip.value}`;

        return value !== chipValueFormatted;
      }),
    );
  };

  const onRefinementKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === "Enter") {
      // TODO: Prevent default "Enter" beahvior
      // and handle sending refinement request here
      event.preventDefault();
    }
  };

  const handleSearchWithLunaToggle = () => {
    const newValue = !isAISearchEnabled;
    if (pathname === "/search") {
      const searchParams = new URLSearchParams(stringify(query));
      searchParams.delete(LangstonParameterSchema.prompt.key);
      void push({
        pathname: "/search",
        search: `?${searchParams.toString()}`,
      });
    }
    onToggle(newValue);
  };

  const {
    handlers: { getSearchParamValues },
  } = useSearchPageContext();
  const needleValue = getSearchParamValues(
    LangstonParameterSchema.prompt.key,
  )?.[0];
  const hasNeedle = Boolean(needleValue && needleValue.trim().length > 0);
  // TODO DISCO-1500: Remove these mock values once we implement their logic.
  const hasLunaError = needleValue === "LUNA_QA_ERROR";
  const isInvalidPrompt = needleValue === "LUNA_QA_REFINEMENT";
  const isLoadingSuggestions = needleValue === "LUNA_QA_LOADING_SUGGESTIONS";
  const isLoadingRefinement = needleValue === "LUNA_QA_LOADING_REFINEMENT";

  const renderBadges = (): ReactNode => (
    <div css={badgesRowCss}>
      <div css={lunaFullTagCss}>
        <AiLunaTagFullIcon />
      </div>
      <AIBetaTag />
    </div>
  );

  const renderErrorState = (): ReactNode => (
    <div css={emptyStateContainerCss} aria-live="polite">
      <h5 css={sectionTitleCss}>Something went wrong.</h5>
      <p css={sectionDescriptionCss}>
        Luna couldn't complete your request right now. Please try again in a few
        moments.
      </p>
      <div css={illustrationAffixBottomRightCss}>
        <LunaConfusedIcon aria-hidden />
      </div>
    </div>
  );

  const renderResults = (): ReactNode => (
    <div>
      <VisibilityPermissions containerCssFn={visibilityPermissionCss} />
      <h5 css={sectionTitleCss}>25 Most relevant results for:</h5>
      <p css={sectionDescriptionCss}>
        "Articles about American Revolution for 8th grade"
      </p>

      <h5 css={subsectionTitleCss}>Filters applied by Luna:</h5>

      <ChipGroup
        groupName={LUNA_FILTER_PANEL_TEST_IDS.CHIP_GROUP}
        chipType={ChipGroup.chipTypes.input}
        onChipEvent={handleChipEvent}
        size={ChipGroup.chipSizes.medium}
        __cssFor={chipsGroupCss}
      >
        {filteredValues.map((item: LunaChip) => (
          <div key={item.value} css={chipItemWrapperCss}>
            <AIChip
              active
              text={item.label}
              value={`${item.type},${item.value}`}
              dataProps={{
                "data-qa-selector": `${LUNA_FILTER_PANEL_TEST_IDS.CHIP_PREFIX}${item.type}-${item.value}`,
                "data-chip-index": String(item.value),
              }}
            />
          </div>
        ))}
      </ChipGroup>

      {renderPromptRefinement()}
    </div>
  );

  const renderPromptRefinement = (): ReactNode => (
    <div css={promptSectionCss}>
      {isLoadingRefinement ? (
        <div css={refinementAreaRootCss} aria-live="polite" aria-busy>
          <div
            css={{
              ...refinementLoadingBoxCss,
              ...(isLoadingRefinement
                ? refinementLoadingVisibleCss
                : refinementLoadingHiddenCss),
            }}
            role="status"
            aria-label="Loading refinement"
            data-testid="luna-refinement-loading"
          >
            <AILoadingSpinner
              dataProps={{ "data-testid": "luna-refinement-spinner" }}
            />
          </div>
        </div>
      ) : (
        <>
          <div css={illustrationAffixTopRightCss}>
            <LunaPeekingSideIcon aria-hidden />
          </div>

          <h5 css={subsectionTitleCss}>Question from Luna:</h5>
          <label htmlFor="luna-refinement-textarea" css={labelPromptCss}>
            Would you like to refine your search by adding one or more topics?
          </label>
          <div css={textareaContainerCss}>
            <textarea
              css={textAreaOverridesCss}
              id="luna-refinement-textarea"
              placeholder="e.g., “Culture” or “History”"
              onKeyDown={onRefinementKeyDown}
              rows={3}
            />
          </div>
        </>
      )}
      {!isLoadingRefinement && (
        <AIButton __cssFor={{ root: primaryActionButtonCss }}>
          Refine search
        </AIButton>
      )}
    </div>
  );

  const renderEmptyState = (): ReactNode => (
    <div css={emptyStateContainerCss} aria-live="polite">
      <h5 css={sectionTitleCss}>Start a new search with Luna.</h5>
      <p css={sectionDescriptionCss}>
        Tell Luna what you need to find. You can improve your search by adding
        topics, grade levels, or types of content to your prompt.
      </p>
      <div css={illustrationAffixBottomRightCss}>
        <LunaThinkingIcon aria-hidden />
      </div>
    </div>
  );

  const renderPromptNeedsRevision = (): ReactNode => (
    <div css={emptyStateContainerCss} aria-live="polite">
      <h5 css={sectionTitleCss}>Please revise your prompt.</h5>
      <p css={sectionDescriptionCss}>
        Luna couldn’t understand your request. To find the best content, use
        specific keywords or clear phrases describing the subject with the grade
        or topics you want.{" "}
      </p>
      <div css={illustrationAffixBottomRightCss}>
        <LunaSearchingIcon aria-hidden />
      </div>
    </div>
  );

  const renderContent = (): ReactNode => {
    if (hasLunaError) return renderErrorState();
    if (isInvalidPrompt) return renderPromptNeedsRevision();
    if (!hasNeedle) return renderEmptyState();
    return renderResults();
  };

  return (
    <div
      css={panelRootCss}
      className={className}
      data-testid={testId || LUNA_FILTER_PANEL_TEST_IDS.CONTAINER}
    >
      <div css={toggleWrapperCss}>
        <AIToggle
          pressed={isAISearchEnabled}
          onToggle={handleSearchWithLunaToggle}
          ariaLabel="Search with Luna"
          labelText="Search with Luna"
          qaSelector="search-with-luna-toggle"
          description="Toggle Search with Luna"
        />
      </div>

      <div css={panelCardCss(isLoadingSuggestions)}>
        {isLoadingSuggestions ? (
          <div css={loadingBoxCss} aria-live="polite">
            <AILoadingSpinner
              dataProps={{ "data-testid": "luna-loading-suggestions-spinner" }}
            />
          </div>
        ) : (
          <>
            {renderBadges()}
            {renderContent()}
          </>
        )}
      </div>
    </div>
  );
};

LunaFilterPanel.displayName = "LunaFilterPanel";
